import { useEffect, useRef } from 'react';
import { useRecentStore, type RecentEntry } from '@/stores/recent.store';

type TrackInput = Omit<RecentEntry, 'ts'> | null | undefined;

/**
 * Auto-track an endpoint visit.
 *
 * Pass the current endpoint summary (or null while loading).
 * The entry is added to the recent store once per (mount + operationId).
 * Re-navigating to a different operationId will track again.
 *
 * Returns the current entries + clear handler for convenience.
 */
export function useRecent(entry: TrackInput) {
    const { entries, add, clear, loadFromStorage } = useRecentStore();
    const lastTrackedRef = useRef<string | null>(null);

    // Hydrate from localStorage on mount
    useEffect(() => {
        loadFromStorage();
    }, [loadFromStorage]);

    // Track endpoint visit — debounced via ref (once per operationId)
    useEffect(() => {
        if (!entry || !entry.operationId) return;
        if (lastTrackedRef.current === entry.operationId) return;
        lastTrackedRef.current = entry.operationId;
        add({
            operationId: entry.operationId,
            method: entry.method,
            path: entry.path,
            summary: entry.summary,
        });
    }, [entry?.operationId, entry?.method, entry?.path, entry?.summary, add, entry]);

    return { entries, clear };
}
